import { useEffect, useState } from "react";
import { useQuery, type UseQueryOptions } from "@tanstack/react-query";

/**
 * Never render a cached success as current after a failed refresh or a stale
 * observation. Existing diagnostic panels retain their controls and loading UI.
 */
export function useAdminObservation<T>(options: UseQueryOptions<T>) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, []);
  const result = useQuery({ staleTime: 0, ...options });
  const interval = typeof options.refetchInterval === "number" ? options.refetchInterval : 60_000;
  const data = result.data as (T & { checkedAt?: string; timestamp?: string; cachedAt?: string }) | undefined;
  const sourceTime = data?.checkedAt ?? data?.timestamp ?? data?.cachedAt;
  const observedAt = sourceTime ? Date.parse(sourceTime) : result.dataUpdatedAt;
  const stale = !!data && (!Number.isFinite(observedAt) || now - observedAt > interval * 3 || observedAt > now + 60_000);
  return {
    ...result,
    data: result.isError || stale ? undefined : result.data,
    isError: result.isError || stale,
  };
}
